import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api';
import AdminLayout from '../components/AdminLayout';
import AccountNavigationTabs from './components/AccountNavigationTabs';
import AccountInfoTab from './components/AccountInfoTab';
import SsoSamlSettingsTab from './components/SsoSamlSettingsTab';
import AccountAuditEventsTab from './components/AccountAuditEventsTab';
import BulkUserUploadTab from './components/BulkUserUploadTab';
import AccountPropertiesTab from './components/AccountPropertiesTab';
import { AdminAccount, AccountTab, Industry, Employee } from './types';

// ========================================
// Styled Components
// ========================================

const InnerContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const AccountTitle = styled.h1`
  font-size: 28px;
  font-weight: 400;
  color: #f2711c;
  margin: 0 0 20px 0;
`;

const TabContent = styled.div`
  background: white;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-top: none;
  border-radius: 0 0 4px 4px;
  padding: 24px;
`;

const LoadingMessage = styled.div`
  padding: 40px;
  text-align: center;
  color: #666;
`;

const ErrorMessage = styled.div`
  padding: 20px;
  background: #fff6f6;
  color: #9f3a38;
  border: 1px solid #e0b4b4;
  border-radius: 4px;
  margin-bottom: 20px;
`;

// ========================================
// Account Context for sharing data across tabs
// ========================================

interface AccountContextType {
  account: AdminAccount | null;
  industries: Industry[];
  employees: Employee[];
  loading: boolean;
  error: string | null;
  refetchAccount: () => Promise<void>;
}

const AccountContext = createContext<AccountContextType | null>(null);

export function useAccountContext() {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccountContext must be used within AccountProvider');
  }
  return context;
}

// ========================================
// Helper to determine active tab from URL
// ========================================

function getActiveTabFromPath(pathname: string): AccountTab {
  if (pathname.includes('/saml')) return 'sso-saml';
  if (pathname.includes('/audit_events')) return 'audit-events';
  if (pathname.includes('/bulk_user_uploads')) return 'bulk-user-upload';
  if (pathname.includes('/properties')) return 'properties';
  return 'account-info';
}

// ========================================
// Component
// ========================================

/**
 * AdminAccountPage - Container component for account management
 * 
 * Migrated from: contently-contently/app/views/admin2/accounts/
 * 
 * This component serves as a container that:
 * - Fetches account data once at the top level
 * - Provides account context to child components
 * - Renders tab navigation
 * - Routes to appropriate tab content
 */
function AdminAccountPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const activeTab = getActiveTabFromPath(location.pathname);
  
  const [account, setAccount] = useState<AdminAccount | null>(null);
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAccount = useCallback(async () => {
    if (!id) return;
    
    try {
      const response = await api.get(`/admin2/accounts/${id}.json`);
      setAccount(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch account:', err);
      setError('Failed to load account data');
    }
  }, [id]);

  const fetchIndustries = useCallback(async () => {
    try {
      const response = await api.get('/admin2/accounts/industries.json');
      setIndustries(response.data);
    } catch (err) {
      console.error('Failed to fetch industries:', err);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await api.get('/admin2/accounts/contently_employees.json');
      setEmployees(response.data);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchAccount(),
        fetchIndustries(),
        fetchEmployees(),
      ]);
      setLoading(false);
    };
    
    loadData();
  }, [fetchAccount, fetchIndustries, fetchEmployees]);

  const refetchAccount = async () => {
    await fetchAccount();
  };

  // Render loading state
  if (loading) {
    return (
      <AdminLayout
        title="Accounts"
        breadcrumb={[{ label: 'Accounts', href: '/admin2/accounts' }, { label: 'Loading...' }]}
      >
        <InnerContentWrapper>
          <LoadingMessage>Loading account data...</LoadingMessage>
        </InnerContentWrapper>
      </AdminLayout>
    );
  }

  // Render error state
  if (error || !account) {
    return (
      <AdminLayout title="Accounts" breadcrumb={[{ label: 'Accounts', href: '/admin2/accounts' }, { label: 'Error' }]}>
        <InnerContentWrapper>
          <ErrorMessage>{error || 'Account not found'}</ErrorMessage>
        </InnerContentWrapper>
      </AdminLayout>
    );
  }

  const contextValue: AccountContextType = {
    account,
    industries,
    employees,
    loading,
    error,
    refetchAccount,
  };

  return (
    <AccountContext.Provider value={contextValue}>
      <AdminLayout
        title="Accounts"
        breadcrumb={[{ label: 'Accounts', href: '/admin2/accounts' }, { label: account.name }]}
      >
        <InnerContentWrapper>
          <AccountTitle>{account.name}</AccountTitle>
          
          <AccountNavigationTabs 
            accountId={account.id} 
            activeTab={activeTab}
          />
          
          <TabContent>
            {activeTab === 'account-info' && <AccountInfoTab />}
            {activeTab === 'sso-saml' && <SsoSamlSettingsTab />}
            {activeTab === 'audit-events' && <AccountAuditEventsTab />}
            {activeTab === 'bulk-user-upload' && <BulkUserUploadTab />}
            {activeTab === 'properties' && <AccountPropertiesTab />}
          </TabContent>
        </InnerContentWrapper>
      </AdminLayout>
    </AccountContext.Provider>
  );
}

export default AdminAccountPage;
